import React, { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import { Transition } from '@headlessui/react';

interface BlacklistUser {
  blacklist_id: string;
  user_id: string;
  user_email: string;
  user_name: string;
  user_phone: string;
  reason: string;
  details: string | null;
  blocked_at: string;
  expires_at: string | null;
  is_active: boolean;
  blocked_by_email: string;
  blocked_by_name: string;
}

interface UserProfile {
  id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
}

interface BlacklistManagementProps {
  supabaseUrl: string;
  supabaseAnonKey: string;
  isKorean: boolean;
  userRole?: 'admin' | 'super_admin' | null;
}

const BlacklistManagement: React.FC<BlacklistManagementProps> = ({ 
  supabaseUrl, 
  supabaseAnonKey, 
  isKorean,
  userRole 
}) => {
  const [blacklistUsers, setBlacklistUsers] = useState<BlacklistUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchingUser, setSearchingUser] = useState(false);
  const [foundUser, setFoundUser] = useState<UserProfile | null>(null);
  
  // Form states for adding user
  const [addEmail, setAddEmail] = useState('');
  const [addReason, setAddReason] = useState('');
  const [addDetails, setAddDetails] = useState('');
  const [addExpires, setAddExpires] = useState('');
  const [addUserId, setAddUserId] = useState('');
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const supabase = createClient(supabaseUrl, supabaseAnonKey);

  useEffect(() => {
    fetchBlacklistUsers();
  }, []);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const fetchBlacklistUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase.rpc('get_admin_blacklist');
      
      if (error) {
        throw error;
      }
      
      setBlacklistUsers(data || []);
    } catch (err: any) {
      console.error('Error fetching blacklist:', err);
      setError(err.message || 'Failed to fetch blacklist');
    } finally {
      setLoading(false);
    }
  };

  const searchUserByEmail = async () => {
    if (!addEmail) return;
    
    try {
      setSearchingUser(true);
      setFoundUser(null);
      
      // Check if environment is DEV or PROD
      const isDev = window.location.hostname === 'localhost' || window.location.hostname.includes('dev');
      const tableName = isDev ? 'D_profiles' : 'P_profiles';
      
      const { data, error } = await supabase
        .from(tableName)
        .select('id, email, full_name, avatar_url')
        .eq('email', addEmail)
        .single();
      
      if (error) {
        setNotification({ 
          message: isKorean ? '사용자를 찾을 수 없습니다.' : 'User not found.',
          type: 'error'
        });
        return;
      }
      
      if (data) {
        setAddUserId(data.id);
        setFoundUser(data);
        setNotification({ 
          message: isKorean ? `사용자 찾음: ${data.full_name || data.email}` : `User found: ${data.full_name || data.email}`,
          type: 'success'
        });
      }
    } catch (err) {
      console.error('Error searching user:', err);
      setNotification({ 
        message: isKorean ? '사용자 검색 실패' : 'Failed to search user',
        type: 'error'
      });
    } finally {
      setSearchingUser(false);
    }
  };

  const addToBlacklist = async () => {
    if (!addUserId) {
      setNotification({ 
        message: isKorean ? '먼저 사용자를 검색하세요.' : 'Please search for user first.',
        type: 'error'
      });
      return;
    }
    
    if (!addReason) {
      setNotification({ 
        message: isKorean ? '차단 사유를 입력하세요.' : 'Please enter blocking reason.',
        type: 'error'
      });
      return;
    }
    
    try {
      setProcessingId('adding');
      
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('Not authenticated');
      }
      
      const { data, error } = await supabase.rpc('add_to_blacklist', {
        p_user_id: addUserId,
        p_reason: addReason,
        p_details: addDetails || null,
        p_expires_at: addExpires ? new Date(addExpires).toISOString() : null
      });
      
      if (error) {
        throw error;
      }
      
      if (data && data.success) {
        // Refresh list
        await fetchBlacklistUsers();
        // Reset form
        setShowAddModal(false);
        setAddEmail('');
        setAddUserId('');
        setAddReason('');
        setAddDetails('');
        setAddExpires('');
        setFoundUser(null);
        setNotification({ 
          message: isKorean ? '블랙리스트에 추가되었습니다.' : 'Added to blacklist successfully.',
          type: 'success'
        });
      } else {
        throw new Error(data?.error || 'Failed to add to blacklist');
      }
    } catch (err: any) {
      console.error('Error adding to blacklist:', err);
      setNotification({ 
        message: isKorean ? `오류: ${err.message}` : `Error: ${err.message}`,
        type: 'error'
      });
    } finally {
      setProcessingId(null);
    }
  };

  const removeFromBlacklist = async (userId: string, userEmail: string) => {
    if (!window.confirm(isKorean ? 
      `${userEmail}님을 블랙리스트에서 제거하시겠습니까?` : 
      `Remove ${userEmail} from blacklist?`)) {
      return;
    }
    
    try {
      setProcessingId(userId);
      
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('Not authenticated');
      }
      
      const { data, error } = await supabase.rpc('remove_from_blacklist', {
        p_user_id: userId
      });
      
      if (error) {
        throw error;
      }
      
      if (data && data.success) {
        await fetchBlacklistUsers();
        setNotification({ 
          message: isKorean ? '블랙리스트에서 제거되었습니다.' : 'Removed from blacklist successfully.',
          type: 'success'
        });
      } else {
        throw new Error(data?.error || 'Failed to remove from blacklist');
      }
    } catch (err: any) {
      console.error('Error removing from blacklist:', err);
      setNotification({ 
        message: isKorean ? `오류: ${err.message}` : `Error: ${err.message}`,
        type: 'error'
      });
    } finally {
      setProcessingId(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString(isKorean ? 'ko-KR' : 'en-US');
  };

  const getReasonBadge = (reason: string) => {
    const reasonMap: { [key: string]: { color: string; text: string; icon: string } } = {
      'spam': { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', text: isKorean ? '스팸' : 'Spam', icon: '📧' },
      'abuse': { color: 'bg-red-100 text-red-800 border-red-200', text: isKorean ? '악용' : 'Abuse', icon: '⚠️' },
      'harassment': { color: 'bg-orange-100 text-orange-800 border-orange-200', text: isKorean ? '괴롭힘' : 'Harassment', icon: '🚫' },
      'fraud': { color: 'bg-purple-100 text-purple-800 border-purple-200', text: isKorean ? '사기' : 'Fraud', icon: '💸' },
      'violation': { color: 'bg-gray-100 text-gray-800 border-gray-200', text: isKorean ? '규정 위반' : 'Violation', icon: '📋' },
      'other': { color: 'bg-blue-100 text-blue-800 border-blue-200', text: isKorean ? '기타' : 'Other', icon: '📌' }
    };
    
    const badge = reasonMap[reason] || reasonMap['other'];
    return (
      <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold border ${badge.color}`}>
        <span className="mr-1">{badge.icon}</span>
        {badge.text}
      </span>
    );
  };

  // Default avatar component
  const DefaultAvatar = ({ name }: { name: string }) => {
    const initials = name ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '?';
    const colors = [
      'bg-gradient-to-br from-blue-400 to-blue-600',
      'bg-gradient-to-br from-green-400 to-green-600',
      'bg-gradient-to-br from-purple-400 to-purple-600',
      'bg-gradient-to-br from-pink-400 to-pink-600',
      'bg-gradient-to-br from-indigo-400 to-indigo-600',
    ];
    const colorIndex = name ? name.charCodeAt(0) % colors.length : 0;
    
    return (
      <div className={`w-12 h-12 rounded-full ${colors[colorIndex]} flex items-center justify-center text-white font-semibold shadow-md`}>
        {initials}
      </div>
    );
  };

  // Filter blacklist based on search
  const filteredBlacklist = blacklistUsers.filter(user => 
    user.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.reason?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (userRole !== 'admin' && userRole !== 'super_admin') {
    return (
      <div className="p-8 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl shadow-lg">
        <div className="flex items-center justify-center">
          <div className="text-center">
            <div className="text-6xl mb-4">🔒</div>
            <div className="text-xl font-bold text-gray-800 mb-2">
              {isKorean ? '접근 제한' : 'Access Restricted'}
            </div>
            <div className="text-gray-600">
              {isKorean ? '관리자 권한이 필요합니다.' : 'Admin permission required.'}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-0">
      {/* Notification */}
      <Transition
        show={!!notification}
        enter="transition-all duration-300 ease-out"
        enterFrom="translate-y-[-100%] opacity-0"
        enterTo="translate-y-0 opacity-100"
        leave="transition-all duration-300 ease-in"
        leaveFrom="translate-y-0 opacity-100"
        leaveTo="translate-y-[-100%] opacity-0"
      >
        <div className="fixed top-4 right-4 z-50">
          {notification && (
            <div className={`px-6 py-4 rounded-xl shadow-2xl backdrop-blur-sm flex items-center space-x-3 ${
              notification.type === 'success' 
                ? 'bg-green-500/90 text-white' 
                : 'bg-red-500/90 text-white'
            }`}>
              <span className="text-xl">
                {notification.type === 'success' ? '✅' : '❌'}
              </span>
              <span className="font-medium">{notification.message}</span>
            </div>
          )}
        </div>
      </Transition>

      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white p-8 rounded-t-2xl">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold flex items-center">
              <span className="mr-3 text-4xl">🛡️</span>
              {isKorean ? '블랙리스트 관리' : 'Blacklist Management'}
            </h2>
            <p className="mt-2 text-gray-300">
              {isKorean ? '차단된 사용자를 관리합니다' : 'Manage blocked users'}
            </p>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-400">{isKorean ? '전체' : 'Total'}</div>
            <div className="text-3xl font-bold">{blacklistUsers.length}</div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-b-2xl shadow-xl">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-4 w-full sm:w-auto">
              <div className="relative flex-1 sm:flex-initial">
                <input
                  type="text"
                  placeholder={isKorean ? '검색 (이메일, 이름, 사유)' : 'Search (email, name, reason)'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2.5 border-2 border-gray-300 rounded-xl w-full sm:w-80 focus:outline-none focus:border-blue-500 transition-all"
                />
                <svg className="absolute left-3 top-3 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <button
                onClick={fetchBlacklistUsers}
                className="px-5 py-2.5 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-all flex items-center space-x-2 shadow-md hover:shadow-lg"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>{isKorean ? '새로고침' : 'Refresh'}</span>
              </button>
            </div>
            
            <button
              onClick={() => setShowAddModal(true)}
              className="px-6 py-2.5 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-700 transition-all flex items-center space-x-2 shadow-md hover:shadow-lg transform hover:scale-105"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
              <span>{isKorean ? '사용자 차단' : 'Block User'}</span>
            </button>
          </div>
          
          {error && (
            <div className="mt-4 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-lg animate-pulse">
              <div className="flex items-center">
                <span className="text-xl mr-2">⚠️</span>
                {error}
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-gray-200 rounded-full animate-pulse"></div>
              <div className="absolute top-0 left-0 w-16 h-16 border-4 border-blue-600 rounded-full animate-spin border-t-transparent"></div>
            </div>
            <p className="mt-4 text-gray-600 font-medium">{isKorean ? '로딩 중...' : 'Loading...'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                    {isKorean ? '사용자' : 'User'}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                    {isKorean ? '차단 사유' : 'Reason'}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                    {isKorean ? '상세 내용' : 'Details'}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                    {isKorean ? '차단일시' : 'Blocked At'}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                    {isKorean ? '만료일' : 'Expires'}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                    {isKorean ? '차단자' : 'Blocked By'}
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-bold text-gray-600 uppercase tracking-wider">
                    {isKorean ? '작업' : 'Actions'}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredBlacklist.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-16 text-center">
                      <div className="flex flex-col items-center">
                        <div className="text-6xl mb-4">📭</div>
                        <div className="text-gray-500 text-lg">
                          {isKorean ? '블랙리스트가 비어있습니다.' : 'No blacklisted users.'}
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredBlacklist.map((user) => (
                    <tr key={user.blacklist_id} className={`${user.is_active ? 'hover:bg-gray-50' : 'opacity-50 bg-gray-50'} transition-colors`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center text-white font-semibold text-sm shadow-sm">
                              {user.user_email?.substring(0, 2).toUpperCase()}
                            </div>
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-gray-900">
                              {user.user_email}
                            </div>
                            {user.user_name && (
                              <div className="text-sm text-gray-600">
                                {user.user_name}
                              </div>
                            )}
                            {user.user_phone && (
                              <div className="text-xs text-gray-400">
                                📞 {user.user_phone}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {getReasonBadge(user.reason)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-700 max-w-xs" title={user.details || ''}>
                          {user.details ? (
                            <div className="truncate">{user.details}</div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-600">
                          📅 {formatDate(user.blocked_at)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {user.expires_at ? (
                          <div className="text-sm text-gray-600">
                            ⏰ {formatDate(user.expires_at)}
                          </div>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold bg-red-100 text-red-700 border border-red-200">
                            ♾️ {isKorean ? '영구' : 'Permanent'}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <div className="text-gray-700 font-medium">{user.blocked_by_email}</div>
                          {user.blocked_by_name && (
                            <div className="text-xs text-gray-500">
                              {user.blocked_by_name}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        {user.is_active && (
                          <button
                            onClick={() => removeFromBlacklist(user.user_id, user.user_email)}
                            disabled={processingId === user.user_id}
                            className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white text-sm font-medium rounded-lg hover:from-green-600 hover:to-green-700 disabled:opacity-50 transition-all transform hover:scale-105 shadow-md hover:shadow-lg"
                          >
                            {processingId === user.user_id ? (
                              <span className="flex items-center space-x-2">
                                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                                </svg>
                              </span>
                            ) : (
                              <span className="flex items-center space-x-1">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span>{isKorean ? '해제' : 'Remove'}</span>
                              </span>
                            )}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Enhanced Add User Modal */}
      <Transition
        show={showAddModal}
        enter="transition-opacity duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-300"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Transition.Child
            enter="transition-all duration-300"
            enterFrom="opacity-0 scale-95 translate-y-4"
            enterTo="opacity-100 scale-100 translate-y-0"
            leave="transition-all duration-300"
            leaveFrom="opacity-100 scale-100 translate-y-0"
            leaveTo="opacity-0 scale-95 translate-y-4"
          >
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-auto overflow-hidden">
              <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-6">
                <h3 className="text-2xl font-bold flex items-center">
                  <span className="mr-3 text-3xl">🚫</span>
                  {isKorean ? '사용자 차단' : 'Block User'}
                </h3>
                <p className="mt-1 text-red-100">
                  {isKorean ? '차단할 사용자 정보를 입력하세요' : 'Enter user information to block'}
                </p>
              </div>
              
              <div className="p-6 space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {isKorean ? '이메일' : 'Email'}
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="email"
                      value={addEmail}
                      onChange={(e) => setAddEmail(e.target.value)}
                      className="flex-1 px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors"
                      placeholder="user@example.com"
                    />
                    <button
                      onClick={searchUserByEmail}
                      disabled={searchingUser || !addEmail}
                      className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center space-x-2 shadow-md hover:shadow-lg"
                    >
                      {searchingUser ? (
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                        </svg>
                      ) : (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                          <span>{isKorean ? '검색' : 'Search'}</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* User Profile Display */}
                {foundUser && (
                  <Transition
                    show={!!foundUser}
                    enter="transition-all duration-300"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                  >
                    <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                      <div className="flex items-center space-x-4">
                        {foundUser.avatar_url ? (
                          <img 
                            src={foundUser.avatar_url} 
                            alt={foundUser.full_name || 'User'}
                            className="w-16 h-16 rounded-full shadow-lg border-2 border-white"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              e.currentTarget.nextElementSibling?.classList.remove('hidden');
                            }}
                          />
                        ) : null}
                        <div className={foundUser.avatar_url ? 'hidden' : ''}>
                          <DefaultAvatar name={foundUser.full_name || foundUser.email} />
                        </div>
                        <div className="flex-1">
                          <div className="text-lg font-semibold text-gray-800">
                            {foundUser.full_name || 'No Name'}
                          </div>
                          <div className="text-sm text-gray-600">
                            {foundUser.email}
                          </div>
                          <div className="mt-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                            ✓ {isKorean ? '확인됨' : 'Verified'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Transition>
                )}
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {isKorean ? '차단 사유' : 'Reason'}
                  </label>
                  <select
                    value={addReason}
                    onChange={(e) => setAddReason(e.target.value)}
                    className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors"
                  >
                    <option value="">{isKorean ? '선택하세요' : 'Select'}</option>
                    <option value="spam">📧 {isKorean ? '스팸' : 'Spam'}</option>
                    <option value="abuse">⚠️ {isKorean ? '악용' : 'Abuse'}</option>
                    <option value="harassment">🚫 {isKorean ? '괴롭힘' : 'Harassment'}</option>
                    <option value="fraud">💸 {isKorean ? '사기' : 'Fraud'}</option>
                    <option value="violation">📋 {isKorean ? '규정 위반' : 'Policy Violation'}</option>
                    <option value="other">📌 {isKorean ? '기타' : 'Other'}</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {isKorean ? '상세 내용 (선택사항)' : 'Details (Optional)'}
                  </label>
                  <textarea
                    value={addDetails}
                    onChange={(e) => setAddDetails(e.target.value)}
                    className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors resize-none"
                    rows={3}
                    placeholder={isKorean ? '추가 설명을 입력하세요...' : 'Enter additional details...'}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {isKorean ? '만료일 (선택사항)' : 'Expiry Date (Optional)'}
                  </label>
                  <input
                    type="datetime-local"
                    value={addExpires}
                    onChange={(e) => setAddExpires(e.target.value)}
                    className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors"
                  />
                  <div className="mt-1 text-xs text-gray-500 flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {isKorean ? '비워두면 영구 차단됩니다' : 'Leave empty for permanent block'}
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setAddEmail('');
                    setAddUserId('');
                    setAddReason('');
                    setAddDetails('');
                    setAddExpires('');
                    setFoundUser(null);
                  }}
                  className="px-5 py-2.5 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
                >
                  {isKorean ? '취소' : 'Cancel'}
                </button>
                <button
                  onClick={addToBlacklist}
                  disabled={!addUserId || !addReason || processingId === 'adding'}
                  className="px-5 py-2.5 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium shadow-md hover:shadow-lg flex items-center space-x-2"
                >
                  {processingId === 'adding' ? (
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                      </svg>
                      <span>{isKorean ? '차단' : 'Block'}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Transition>
    </div>
  );
};

export default BlacklistManagement;